import React, { useState, useEffect } from 'react';
import { 
    Form, 
    Input, 
    Button, 
    InputNumber, 
    Select, 
    Space, 
    Card, 
    Alert, 
    Modal, 
    Row, 
    Col, 
    Typography, 
    Divider, 
    Tag, 
    Tabs, 
    List, 
    Switch,
    message 
} from 'antd';
import { 
    PlusOutlined, 
    DeleteOutlined, 
    EditOutlined, 
    ArrowRightOutlined, 
    NotificationOutlined,
    InfoCircleOutlined,
    SettingOutlined,
    UserOutlined,
    FormOutlined,
    BellOutlined,
    LockOutlined,
    CloseOutlined
} from '@ant-design/icons';
import { AccessControlForm } from './AccessControlForm';
import { NotificationConfigurationSelector } from './NotificationConfigurationSelector';
import {CustomNode, NodeData} from "@/modules/action-flow/config";

const { Option } = Select;
const { TextArea } = Input;
const { Text } = Typography;
const { TabPane } = Tabs;

interface NodeEditorProps {
    node: CustomNode;
    updateNode: (id: string, data: Partial<NodeData>) => void;
    nodes: CustomNode[];
    maxLevel: number;
    visible: boolean;
    onClose: () => void;
}

export const NodeEditor: React.FC<NodeEditorProps> = ({
                                                          node,
                                                          updateNode,
                                                          nodes,
                                                          maxLevel,
                                                          visible,
                                                          onClose,
                                                          ...rest
                                                      }) => {
    const [form] = Form.useForm();
    const [actionModalVisible, setActionModalVisible] = useState(false);
    const [currentActionIndex, setCurrentActionIndex] = useState<number | null>(null);
    const [actionForm] = Form.useForm();
    const [actions, setActions] = useState<any[]>([]);

    // Initialize form with node data
    useEffect(() => {
        if (visible && node) {
            form.setFieldsValue({
                ...node.data,
            });
            setActions(node.data.actions || []);
        }
    }, [form, node, visible]);

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            // Debug: Log all form values to understand the structure
            console.log('=== FORM VALUES DEBUG ===');
            console.log('Full form values:', values);
            console.log('Actions from form:', values.actions);
            console.log('Actions state:', actions);
            console.log('Access controls from form:', values.accessControls);
            console.log('Node type:', node.type);
            console.log('========================');
            
            // For validator and maker nodes, validate the actions-access controls relationship
            if (node.type === 'validator' || node.type === 'maker') {
                // Use actions from state if form doesn't have them, or form if it does
                const formActions = values.actions || [];
                const stateActions = actions || [];
                const actualActions = formActions.length > 0 ? formActions : stateActions;
                
                const hasActions = actualActions && actualActions.length > 0;
                const hasAccessControls = values.accessControls && values.accessControls.length > 0;
                
                console.log('Validation check:');
                console.log('hasActions:', hasActions);
                console.log('hasAccessControls:', hasAccessControls);
                
                // Only enforce validation if BOTH are configured or if one is configured without the other
                if (hasActions && !hasAccessControls) {
                    message.error('Nodes with actions must have at least one access control configured.');
                    return;
                }
                
                if (hasAccessControls && !hasActions) {
                    message.error('Nodes with access controls must have at least one action configured.');
                    return;
                }
                
                // Allow saving if both are empty (user can configure them later)
                // Allow saving if both are configured (valid state)
            }
            
            // Ensure actions from state are included in the final values
            const finalValues = {
                ...values,
                actions: actions.length > 0 ? actions : values.actions || []
            };
            
            updateNode(node.id, finalValues);
            onClose(); // Close modal after successful update
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const openActionModal = (index: number | null) => {
        setCurrentActionIndex(index);
        
        if (index !== null) {
            // Edit existing action
            const action = form.getFieldValue(['actions', index]);
            actionForm.setFieldsValue(action);
        } else {
            // New action
            actionForm.resetFields();
            
            // Pre-populate event name based on node ID
            if (node && node.id) {
                actionForm.setFieldValue('name', 'APPROVE');
                actionForm.setFieldValue('event', `${node.id}_APPROVE`);
            }
        }
        
        setActionModalVisible(true);
    };

    const handleActionModalOk = async () => {
        try {
            const values = await actionForm.validateFields();
            const currentActions = form.getFieldValue('actions') || [];
            
            if (currentActionIndex !== null) {
                // Update existing action
                currentActions[currentActionIndex] = values;
            } else {
                // Add new action
                currentActions.push(values);
            }
            
            // Update the form field
            form.setFieldValue('actions', currentActions);
            setActions(currentActions);
            
            // Just update the node data without validation (validation happens on final save)
            updateNode(node.id, { actions: currentActions });
            
            setActionModalVisible(false);
        } catch (error) {
            console.error('Action form validation failed:', error);
        }
    };

    const handleActionModalCancel = () => {
        setActionModalVisible(false);
        setCurrentActionIndex(null);
        actionForm.resetFields();
    };

    const handleActionTypeChange = (value: string) => {
        if (value === 'REROUTE') {
            const nextNode = actionForm.getFieldValue('nextNode');
            if (nextNode) {
                const eventName = `${node.id}_REROUTE_${nextNode}`;
                actionForm.setFieldValue('event', eventName);
            }
        } else {
            const eventName = `${node.id}_${value}`;
            actionForm.setFieldValue('event', eventName);
        }
    };

    const handleNextNodeChange = (value: string) => {
        const actionType = actionForm.getFieldValue('name');
        if (actionType === 'REROUTE') {
            const eventName = `${node.id}_REROUTE_${value}`;
            actionForm.setFieldValue('event', eventName);
        }
    };

    const handleDeleteAction = async (index: number) => {
        const currentActions = [...actions];
        currentActions.splice(index, 1);
        form.setFieldValue('actions', currentActions);
        setActions(currentActions);
        
        // Just update the node data without validation (validation happens on final save)
        updateNode(node.id, { actions: currentActions });
    };

    // Render a compact summary of actions in the sidebar
    const renderActionsSummary = () => {
        return (
            <List
                size="small"
                dataSource={form.getFieldValue('actions') || []}
                renderItem={(action, index) => (
                    <List.Item
                        key={index}
                        actions={[
                            <Button
                                key="edit"
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => openActionModal(index)}
                            />,
                            <Button
                                key="delete"
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDeleteAction(index)}
                            />
                        ]}
                    >
                        <List.Item.Meta
                            title={<Text strong>{
                                // @ts-ignore
                                action.label || action.name}</Text>}
                            description={
                            // @ts-ignore
                            `Event: ${action.event}${
                                // @ts-ignore
                                action.nextNode ? ` → ${nodes.find(n => n.id === action.nextNode)?.data.label}` : ''
                            }`}
                        />
                    </List.Item>
                )}
                locale={{ emptyText: 'No actions configured' }}
            />
        );
    };

    // Set initial form values
    React.useEffect(() => {
        form.setFieldsValue(node.data);
        
        // Auto-generate event names for actions if they don't exist
        const actions = node.data.actions || [];
        const updatedActions = actions.map(action => {
            if (!action.event) {
                if (action.name === 'REROUTE' && action.nextNode) {
                    action.event = `${node.id}_REROUTE_${action.nextNode}`;
                } else {
                    action.event = `${node.id}_${action.name}`;
                }
            }
            return action;
        });
        
        if (updatedActions.length > 0 && JSON.stringify(actions) !== JSON.stringify(updatedActions)) {
            form.setFieldValue('actions', updatedActions);
        }
    }, [form, node]);

    return (
        <Modal
            title={`Edit Node: ${node.data.label || 'Unnamed Node'}`}
            open={visible}
            onCancel={onClose}
            width={800}
            styles={{ body: { maxHeight: '80vh', overflowY: 'auto', padding: '20px' } }}
            footer={null}
            destroyOnClose={true}
            style={{ zIndex: 1050 }}
            {...rest}
        >
            <Form
                form={form}
                layout="vertical"
                initialValues={node.data}
                onFinish={handleSubmit}
            >
                <Tabs 
                    defaultActiveKey="basic" 
                    style={{ marginBottom: '16px' }}
                    type="card"
                    size="large"
                >
                    <TabPane tab={<span><InfoCircleOutlined /> Details</span>} key="basic">
                        <Card size="small" className="mb-4">
                            <Form.Item
                                name="label"
                                label="Label"
                                rules={[{ required: true, message: 'Label is required' }]}
                            >
                                <Input />
                            </Form.Item>

                            <Form.Item
                                name="description"
                                label="Description"
                            >
                                <TextArea rows={3} />
                            </Form.Item>

                            <Form.Item
                                name="level"
                                label="Level"
                                rules={[{ required: true, message: 'Level is required' }]}
                            >
                                <InputNumber
                                    min={0}
                                    max={maxLevel}
                                    disabled={node.type === 'maker' || node.type === 'approved' || node.type === 'rejected'}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        </Card>
                    </TabPane>
                    
                    {node.type !== 'approved' && node.type !== 'rejected' && (
                        <TabPane tab={<span><SettingOutlined /> Configuration</span>} key="configuration">
                            {(node.type === 'validator' || node.type === 'maker') && (
                                <>
                                    <Card title="Actions" size="small" className="mb-4">
                                        {renderActionsSummary()}
                                        
                                        <Divider style={{ margin: '12px 0' }} />
                                        
                                        <Button
                                            type="dashed"
                                            onClick={() => openActionModal(null)}
                                            block
                                            icon={<PlusOutlined />}
                                        >
                                            Add Action
                                        </Button>
                                    </Card>
                                
                                    {node.type === 'validator' && (
                                        <Card title="Approval Configuration" size="small" className="mb-4">
                                            <Space>
                                                <Form.Item
                                                    name={['approvalConfig', 'minApprovals']}
                                                    label="Minimum Approvals"
                                                    rules={[{ required: true }]}
                                                >
                                                    <InputNumber min={1} />
                                                </Form.Item>

                                                <Form.Item
                                                    name={['approvalConfig', 'totalApprovals']}
                                                    label="Total Approvals"
                                                    rules={[{ required: true }]}
                                                >
                                                    <InputNumber min={1} />
                                                </Form.Item>
                                            </Space>
                                        </Card>
                                    )}
                                    
                                    <Card title="Access Controls" size="small" className="mb-4">
                                        <Form.Item 
                                            name="accessControls"
                                            help="Define who can perform actions on this node"
                                        >
                                            <AccessControlForm />
                                        </Form.Item>
                                    </Card>
                                </>
                            )}
                            
                            {node.type === 'router' && (
                                <>
                                    <Form.List name="decisionRules">
                                        {(fields, { add, remove }) => (
                                            <Card title="Decision Rules" size="small" className="mb-4">
                                                {fields.map((field) => (
                                                    <Space key={field.key} className="flex mb-2" direction="vertical">
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'expression']}
                                                            rules={[{ required: true }]}
                                                            label="Expression"
                                                        >
                                                            <TextArea rows={2} />
                                                        </Form.Item>

                                                        <Space>
                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, 'event']}
                                                                rules={[{ required: true }]}
                                                                label="Event"
                                                            >
                                                                <Input />
                                                            </Form.Item>

                                                            <Form.Item
                                                                {...field}
                                                                name={[field.name, 'nextNode']}
                                                                rules={[{ required: true }]}
                                                                label="Next Node"
                                                            >
                                                                <Select style={{ width: 160 }}>
                                                                    {nodes
                                                                        .filter((n) => n.id !== node.id)
                                                                        .map((n) => (
                                                                            <Option key={n.id} value={n.id}>
                                                                                {n.data.label}
                                                                            </Option>
                                                                        ))}
                                                                </Select>
                                                            </Form.Item>

                                                            <Button
                                                                type="text"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => remove(field.name)}
                                                            />
                                                        </Space>
                                                    </Space>
                                                ))}

                                                <Button
                                                    type="dashed"
                                                    onClick={() => add()}
                                                    block
                                                    icon={<PlusOutlined />}
                                                >
                                                    Add Rule
                                                </Button>
                                            </Card>
                                        )}
                                    </Form.List>

                                    <Card title="Default Routing" size="small" className="mb-4">
                                        <Form.Item name="defaultEvent" label="Default Event">
                                            <Input />
                                        </Form.Item>

                                        <Form.Item name="defaultNextNode" label="Default Next Node">
                                            <Select>
                                                {nodes
                                                    .filter((n) => n.id !== node.id)
                                                    .map((n) => (
                                                        <Option key={n.id} value={n.id}>
                                                            {n.data.label}
                                                        </Option>
                                                    ))}
                                            </Select>
                                        </Form.Item>
                                    </Card>
                                </>
                            )}
                            
                            {node.type === 'executor' && (
                                <>
                                    <Card title="Bean Configuration" size="small" className="mb-4">
                                        <Form.Item name="beanName" label="Bean Name" rules={[{ required: true }]}>
                                            <Input />
                                        </Form.Item>
                                    </Card>
                                    
                                    <Form.List name="transitions">
                                        {(fields, { add, remove }) => (
                                            <Card title="Transitions" size="small" className="mb-4">
                                                {fields.map((field) => (
                                                    <Space key={field.key} className="flex mb-2" align="baseline">
                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'event']}
                                                            rules={[{ required: true }]}
                                                        >
                                                            <Input placeholder="Event" />
                                                        </Form.Item>

                                                        <Form.Item
                                                            {...field}
                                                            name={[field.name, 'nextNode']}
                                                            rules={[{ required: true }]}
                                                        >
                                                            <Select style={{ width: 160 }}>
                                                                {nodes
                                                                    .filter((n) => n.id !== node.id)
                                                                    .map((n) => (
                                                                        <Option key={n.id} value={n.id}>
                                                                            {n.data.label}
                                                                        </Option>
                                                                    ))}
                                                            </Select>
                                                        </Form.Item>

                                                        <Button
                                                            type="text"
                                                            danger
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => remove(field.name)}
                                                        />
                                                    </Space>
                                                ))}

                                                <Button
                                                    type="dashed"
                                                    onClick={() => add()}
                                                    block
                                                    icon={<PlusOutlined />}
                                                >
                                                    Add Transition
                                                </Button>
                                            </Card>
                                        )}
                                    </Form.List>
                                </>
                            )}
                        </TabPane>
                    )}
                    
                    <TabPane tab={<span><BellOutlined /> Advanced</span>} key="advanced">
                        <Card title="Notifications" size="small" className="mb-4">
                            <Form.Item
                                name="notificationConfigurations"
                                label="Notification Configurations"
                                help={
                                    node.type === 'validator'
                                        ? "Select one or more notification configurations to be sent when this node is activated"
                                        : node.type === 'maker'
                                            ? "Select one or more notification configurations to be sent when this node is activated or when workflows are returned to the maker"
                                            : node.type === 'router'
                                                ? "Select one or more notification configurations to be sent when this node makes routing decisions"
                                                : node.type === 'executor'
                                                    ? "Select one or more notification configurations to be sent when this executor node is processed"
                                                    : node.type === 'approved' || node.type === 'rejected'
                                                        ? "Select one or more notification configurations to be sent when this node is reached"
                                                        : "Select one or more notification configurations to be sent when this node is activated"
                                }
                            >
                                <NotificationConfigurationSelector />
                            </Form.Item>
                        </Card>
                        
                    </TabPane>
                    
                    {(node.type === 'approved' || node.type === 'rejected') && (
                        <TabPane tab={<span><SettingOutlined /> Terminal State</span>} key="terminal">
                            <Alert
                                message="End State"
                                description="This is a terminal node and does not require additional configuration."
                                type="info"
                            />
                        </TabPane>
                    )}
                </Tabs>
                
                <Form.Item>
                    <Button type="primary" onClick={handleSubmit} block>
                        Update Node
                    </Button>
                </Form.Item>
                
                {/* Action Configuration Modal */}
                <Modal
                    title="Configure Action"
                    open={actionModalVisible}
                    onOk={handleActionModalOk}
                    onCancel={handleActionModalCancel}
                    width={700}
                >
                    <Form
                        form={actionForm}
                        layout="vertical"
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label="Action Type"
                                    rules={[{ required: true, message: 'Action type required' }]}
                                >
                                    <Select 
                                        onChange={handleActionTypeChange}
                                    >
                                        <Option value="APPROVE">Approve</Option>
                                        <Option value="REJECT">Reject</Option>
                                        <Option value="RETURN">Return</Option>
                                        <Option value="REROUTE">Reroute</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="label"
                                    label="Display Label"
                                    rules={[{ required: true, message: 'Label required' }]}
                                >
                                    <Input placeholder="Action Label" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) =>
                                prevValues?.name !== currentValues?.name
                            }
                        >
                            {({ getFieldValue }) => {
                                const actionName = getFieldValue('name');
                                return actionName === 'REROUTE' ? (
                                    <Form.Item
                                        name="nextNode"
                                        label="Target Node"
                                        rules={[{ required: true, message: 'Target node required' }]}
                                    >
                                        <Select onChange={handleNextNodeChange}>
                                            {nodes
                                                .filter((n) => n.id !== node.id)
                                                .map((n) => (
                                                    <Option key={n.id} value={n.id}>
                                                        {n.data.label}
                                                    </Option>
                                                ))}
                                        </Select>
                                    </Form.Item>
                                ) : null;
                            }}
                        </Form.Item>

                        <Form.Item
                            name="event"
                            label="Event Name"
                            rules={[{ required: true, message: 'Event name required' }]}
                            help="This event name will be used for state machine transitions"
                        >
                            <Input placeholder="Event Name" />
                        </Form.Item>
                    </Form>
                </Modal>
            </Form>
        </Modal>
    );
};